import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import TabHeader from "@/components/admin/TabHeader";
import { field } from "@/pages/admin/AdminLogin";

function TempPassword({ info, onClose }) {
  return (
    <div data-testid="team-temp-password" className="mb-6 border border-clay/40 bg-[#FBF6F1] p-5 font-body text-sm text-ink">
      <p>Share these sign-in details with <b>{info.email}</b> privately. They’ll be asked to choose their own password on first sign-in.</p>
      <p className="mt-3">Sign-in page: <span className="font-mono">{window.location.origin}/admin</span></p>
      <p className="mt-1">Temporary password: <span data-testid="team-temp-password-value" className="font-mono">{info.password}</span></p>
      <button type="button" onClick={onClose} className="mt-3 text-xs text-muted underline">Done</button>
    </div>
  );
}

export default function TeamTab() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ email: "", name: "" });
  const [temp, setTemp] = useState(null);
  const load = () => api.get("/admin/users").then((r) => setUsers(r.data));
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/admin/users", { ...form, role: "editor" });
      setTemp({ email: data.user.email, password: data.temp_password });
      setForm({ email: "", name: "" });
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };
  const reset = async (u) => {
    const { data } = await api.post(`/admin/users/${u.id}/reset-password`);
    setTemp({ email: u.email, password: data.temp_password });
  };
  const remove = async (u) => {
    if (!window.confirm(`Remove ${u.email}? They will lose access.`)) return;
    await api.delete(`/admin/users/${u.id}`);
    load();
  };

  return (
    <div data-testid="admin-team-tab">
      <TabHeader title="Team" note="Editors can update store items, rooms, imagery and orders. Only you (the owner) can manage people, shipping and connected accounts." />
      {temp && <TempPassword info={temp} onClose={() => setTemp(null)} />}
      <form onSubmit={add} className="mb-6 flex flex-wrap gap-3 bg-paper p-5">
        <input data-testid="team-new-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Name" className={`${field} max-w-[12rem]`} />
        <input data-testid="team-new-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="their@email.com" className={`${field} max-w-xs`} />
        <button data-testid="team-add-btn" className="bg-ink px-5 text-paper"><span className="overline">Add editor</span></button>
      </form>
      <div className="divide-y divide-hairline bg-paper">
        {users.map((u) => (
          <div key={u.id} data-testid={`team-user-${u.email}`} className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 font-body text-sm">
            <div><p className="text-ink">{u.name || u.email}</p><p className="text-xs text-muted">{u.email} · {u.role}{u.must_change_password ? " · hasn’t signed in yet" : ""}</p></div>
            {u.id !== user.id && (
              <div className="flex gap-4 text-xs">
                <button type="button" data-testid={`team-reset-${u.email}`} onClick={() => reset(u)} className="text-stone hover:text-ink">Reset password</button>
                <button type="button" data-testid={`team-remove-${u.email}`} onClick={() => remove(u)} className="text-stone hover:text-red-700">Remove</button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
